import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Tabs, Table, Button, Form, Input, Select, InputNumber, Space, Tag, Modal,
  Card, Row, Col, Statistic, message, Popconfirm, Progress, Typography, Radio, Alert, Tooltip
} from 'antd';
import {
  SwapOutlined, KeyOutlined,
  SyncOutlined, SendOutlined, RollbackOutlined,
  StopOutlined, WalletOutlined
} from '@ant-design/icons';
import { apiGet, apiPost } from '../services/api';

const { Text } = Typography;
const { Option } = Select;

const COLORS = {
  primary: '#1677ff',
  success: '#52c41a',
  error: '#ff4d4f',
  warning: '#faad14',
};

// ===== Types =====

interface Market {
  id?: string | number;
  conditionId?: string;
  condition_id?: string;
  question?: string;
  slug?: string;
  // Gamma API: clobTokenIds 是 JSON 字符串, outcomes 也是 JSON 字符串
  clobTokenIds?: string;
  outcomes?: string;
  // 解析后的字段 (前端规范化后)
  tokens?: { token_id: string; outcome: string }[];
}

interface BotStatus {
  id?: number;
  passportId: number;
  label?: string;
  walletAddress: string;    // EOA 签名地址
  proxyAddress?: string;    // Polymarket Proxy 持仓地址
  usdcBalance: number;
  usdcApproved: boolean;
  ctApproved: boolean;
  apiKeyRegistered: boolean;
  lastSyncedAt?: string;
}

// 生成 Polygonscan 链接
const polygonscanLink = (address: string) => `https://polygonscan.com/address/${address}`;
// Polymarket 官方浏览器
const polymarketUserLink = (address: string) => `https://polymarket.com/profile/${address}`;

interface OrderRecord {
  id: number;
  passportId: number;
  walletAddress: string;
  orderId: string;
  tokenId: string;
  marketTitle: string;
  side: string;
  price: number;
  size: number;
  orderType: string;
  status: string;
  filledSize: number;
  errorMessage: string;
  createTime: string;
}

interface BatchTask {
  taskId: string;
  taskType: string;
  totalCount: number;
  successCount: number;
  failedCount: number;
  status: string;
}

// ===== Component =====

const Polymarket: React.FC = () => {
  // Trading state
  const [markets, setMarkets] = useState<Market[]>([]);
  const [selectedMarket, setSelectedMarket] = useState<Market | null>(null);
  const [selectedTokenId, setSelectedTokenId] = useState<string>('');
  const [orderBook, setOrderBook] = useState<any>(null);
  const [orderForm] = Form.useForm();

  // Orders state
  const [openOrders, setOpenOrders] = useState<OrderRecord[]>([]);
  const [orderHistory, setOrderHistory] = useState<OrderRecord[]>([]);
  const [orderBotId, setOrderBotId] = useState<number | null>(null);

  // Bot status state
  const [botStatuses, setBotStatuses] = useState<BotStatus[]>([]);
  const [selectedBotIds, setSelectedBotIds] = useState<number[]>([]);

  // Task polling
  const [currentTask, setCurrentTask] = useState<BatchTask | null>(null);
  const taskPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Market search debounce
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Loading
  const [loading, setLoading] = useState(false);
  // masterBalance 移除: 运营输入私钥时实时查询

  // ===== Data fetching =====

  // 规范化 Gamma API 返回的市场数据:
  //   clobTokenIds 是 JSON 字符串 "[\"tokenA\", \"tokenB\"]"
  //   outcomes 是 JSON 字符串 "[\"Yes\", \"No\"]"
  const normalizeMarket = (raw: any): Market => {
    let tokens: { token_id: string; outcome: string }[] = [];
    try {
      const tokenIds = typeof raw.clobTokenIds === 'string'
        ? JSON.parse(raw.clobTokenIds)
        : raw.clobTokenIds;
      const outcomes = typeof raw.outcomes === 'string'
        ? JSON.parse(raw.outcomes)
        : raw.outcomes;
      if (Array.isArray(tokenIds) && Array.isArray(outcomes)) {
        tokens = tokenIds.map((tid: string, i: number) => ({
          token_id: String(tid),
          outcome: outcomes[i] || `Outcome ${i}`,
        }));
      }
    } catch (e) {
      console.warn('Failed to parse market tokens/outcomes', raw);
    }
    return {
      ...raw,
      condition_id: raw.conditionId || raw.condition_id || String(raw.id || ''),
      tokens,
    };
  };

  const fetchMarkets = useCallback(async (query?: string) => {
    try {
      const url = query
        ? `/api/polymarket/markets?query=${encodeURIComponent(query)}&limit=50`
        : '/api/polymarket/markets?limit=50';
      const res = await apiGet<any>(url);
      if (res.code === 0 && res.data) {
        const raw = Array.isArray(res.data) ? res.data : [];
        setMarkets(raw.map(normalizeMarket));
      }
    } catch (e) {
      console.error('Failed to fetch markets', e);
    }
  }, []);

  const fetchOrderBook = useCallback(async (tokenId: string) => {
    try {
      const res = await apiGet<any>(`/api/polymarket/book?tokenId=${tokenId}`);
      if (res.code === 0) setOrderBook(res.data);
    } catch (e) {
      console.error('Failed to fetch order book', e);
    }
  }, []);

  const fetchBotStatuses = useCallback(async () => {
    try {
      const res = await apiGet<any>('/api/polymarket/bot-balances');
      if (res.code === 0 && res.data) setBotStatuses(res.data);
    } catch (e) {
      console.error('Failed to fetch bot statuses', e);
    }
  }, []);

  // 主钱包信息在运营输入私钥后通过后端验证 (Modal 内处理)

  const fetchOpenOrders = useCallback(async (passportId: number) => {
    try {
      const res = await apiGet<any>(`/api/polymarket/orders?passportId=${passportId}`);
      if (res.code === 0 && res.data) setOpenOrders(res.data);
    } catch (e) {
      console.error('Failed to fetch open orders', e);
    }
  }, []);

  const fetchOrderHistory = useCallback(async (passportId: number) => {
    try {
      const res = await apiGet<any>(`/api/polymarket/order-history?passportId=${passportId}&limit=50`);
      if (res.code === 0 && res.data) setOrderHistory(res.data);
    } catch (e) {
      console.error('Failed to fetch order history', e);
    }
  }, []);

  useEffect(() => {
    fetchMarkets();
    fetchBotStatuses();
  }, [fetchMarkets, fetchBotStatuses]);

  // ===== Task polling with localStorage persistence =====
  // 批量任务的 taskId 存 localStorage, 刷新页面后自动恢复轮询
  const TASK_STORAGE_KEY = 'polymarket_active_task_id';

  const stopTaskPolling = useCallback(() => {
    if (taskPollingRef.current) {
      clearInterval(taskPollingRef.current);
      taskPollingRef.current = null;
    }
    localStorage.removeItem(TASK_STORAGE_KEY);
  }, []);

  const startTaskPolling = useCallback((taskId: string) => {
    if (taskPollingRef.current) clearInterval(taskPollingRef.current);
    localStorage.setItem(TASK_STORAGE_KEY, taskId);

    const poll = async () => {
      try {
        const res = await apiGet<any>(`/api/polymarket/task/${taskId}`);
        if (res.code === 0 && res.data) {
          setCurrentTask(res.data);
          if (res.data.status === 'completed' || res.data.status === 'failed') {
            if (taskPollingRef.current) clearInterval(taskPollingRef.current);
            taskPollingRef.current = null;
            localStorage.removeItem(TASK_STORAGE_KEY);
            fetchBotStatuses();
            if (res.data.status === 'completed') {
              message.success('任务完成');
            } else {
              message.error('任务失败: ' + (res.data.errorMessage || '未知错误'));
            }
          }
        } else if (res.code === 404) {
          // 任务不存在 (可能是旧的已清理), 停止轮询
          stopTaskPolling();
          setCurrentTask(null);
        }
      } catch (e) {
        console.error('Failed to poll task', e);
      }
    };

    poll(); // 立即查一次, 不用等 2s
    taskPollingRef.current = setInterval(poll, 2000);
  }, [fetchBotStatuses, stopTaskPolling]);

  // 页面挂载时, 恢复未完成的任务轮询
  useEffect(() => {
    const storedTaskId = localStorage.getItem(TASK_STORAGE_KEY);
    if (storedTaskId) {
      console.log('Resuming task polling from localStorage:', storedTaskId);
      startTaskPolling(storedTaskId);
    }
    return () => {
      if (taskPollingRef.current) clearInterval(taskPollingRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== Handlers =====

  const handlePlaceOrder = async (values: any) => {
    try {
      setLoading(true);
      const passportIds = values.passportIds as number[];
      const payload = {
        tokenId: selectedTokenId,
        marketTitle: selectedMarket?.question || '',
        side: values.side,
        price: values.price,
        size: values.size,
        orderType: values.orderType || 'GTC',
        passportIds,
      };

      // 统一走异步任务, 单笔/批量同一路径, 刷新页面不丢状态
      const res = await apiPost<any>('/api/polymarket/batch-order', payload);
      if (res.code === 0) {
        message.info(`下单任务已创建 (${passportIds.length} 笔)`);
        startTaskPolling(res.data.taskId);
      } else {
        message.error(res.msg);
      }
    } catch (e: any) {
      message.error('下单失败: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = async (passportId: number, orderId: string) => {
    try {
      const res = await apiPost<any>('/api/polymarket/cancel-order', { passportId, orderId });
      if (res.code === 0) {
        const data = res.data;
        if (data.status === 'matched') {
          message.warning(`已全部成交 (${data.filledSize}), 无法撤回`);
        } else if (data.status === 'partial') {
          message.info(data.message);
        } else {
          message.success('撤单成功');
        }
        if (orderBotId) fetchOpenOrders(orderBotId);
      } else {
        message.error(res.msg);
      }
    } catch (e: any) {
      message.error('撤单失败: ' + e.message);
    }
  };

  const handleBatchAction = async (action: string, extra?: any) => {
    if (selectedBotIds.length === 0) {
      message.warning('请先选择 Bot');
      return;
    }
    try {
      setLoading(true);
      let url = '';
      let body: any = { passportIds: selectedBotIds };

      switch (action) {
        case 'approve':
          url = '/api/polymarket/approve';
          break;
        case 'register-keys':
          url = '/api/polymarket/register-keys';
          break;
        case 'distribute':
          url = '/api/polymarket/distribute';
          body.amountPerBot = extra?.amount;
          body.masterPrivateKey = extra?.masterPrivateKey;
          break;
        case 'collect':
          url = '/api/polymarket/collect';
          body.masterAddress = extra?.masterAddress;
          break;
        case 'sync':
          url = '/api/polymarket/sync-balances';
          break;
        default:
          return;
      }

      const res = await apiPost<any>(url, body);
      if (res.code === 0) {
        if (res.data?.taskId) {
          message.info('任务已创建');
          startTaskPolling(res.data.taskId);
        } else {
          message.success('操作完成');
          fetchBotStatuses();
        }
      } else {
        message.error(res.msg);
      }
    } catch (e: any) {
      message.error('操作失败: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  // ===== Status tag helper =====
  const statusTag = (status: string) => {
    const map: Record<string, { color: string; text: string }> = {
      pending: { color: 'default', text: '待处理' },
      open: { color: 'processing', text: '挂单中' },
      matched: { color: 'success', text: '已成交' },
      cancelled: { color: 'warning', text: '已撤销' },
      failed: { color: 'error', text: '失败' },
      partial: { color: 'orange', text: '部分成交' },
      running: { color: 'processing', text: '执行中' },
      completed: { color: 'success', text: '已完成' },
    };
    const s = map[status] || { color: 'default', text: status };
    return <Tag color={s.color}>{s.text}</Tag>;
  };

  // ===== Render Tabs =====

  const renderTradeTab = () => (
    <>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="下单前请先在 Bot 准备 Tab 完成: 批量授权 + 批量分发 pUSD。API Key 首次下单时自动注册。"
      />
    <Row gutter={16}>
      <Col span={12}>
        <Card title="选择市场" size="small">
          <Select
            showSearch
            placeholder="输入关键词 (trump) 或 slug (strait-of-hormuz-...)"
            style={{ width: '100%', marginBottom: 16 }}
            filterOption={false}
            onSearch={(input) => {
              if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
              searchDebounceRef.current = setTimeout(() => {
                fetchMarkets(input);
              }, 400);
            }}
            notFoundContent={markets.length === 0 ? '输入关键词搜索' : '未找到匹配'}
            onChange={async (val: string) => {
              const m = markets.find(m => m.condition_id === val);
              if (!m) return;
              // public-search 返回的 market 可能缺 clobTokenIds, 需要补齐
              if (!m.tokens || m.tokens.length === 0) {
                try {
                  const res = await apiGet<any>(`/api/polymarket/market?conditionId=${encodeURIComponent(val)}`);
                  if (res.code === 0 && res.data) {
                    const full = normalizeMarket(res.data);
                    setSelectedMarket(full);
                    if (full.tokens && full.tokens.length > 0) {
                      setSelectedTokenId(full.tokens[0].token_id);
                      fetchOrderBook(full.tokens[0].token_id);
                    }
                    return;
                  }
                } catch (e) {
                  console.error('补齐市场信息失败', e);
                }
              }
              setSelectedMarket(m);
              if (m.tokens && m.tokens.length > 0) {
                setSelectedTokenId(m.tokens[0].token_id);
                fetchOrderBook(m.tokens[0].token_id);
              }
            }}
          >
            {markets.map(m => (
              <Option key={m.condition_id} value={m.condition_id}>
                {m.question}
              </Option>
            ))}
          </Select>

          {selectedMarket && selectedMarket.tokens && (
            <Radio.Group
              value={selectedTokenId}
              onChange={e => {
                setSelectedTokenId(e.target.value);
                fetchOrderBook(e.target.value);
              }}
              style={{ marginBottom: 16 }}
            >
              {selectedMarket.tokens.map(t => (
                <Radio.Button key={t.token_id} value={t.token_id}>
                  {t.outcome}
                </Radio.Button>
              ))}
            </Radio.Group>
          )}

          {orderBook && (
            <div>
              <Text strong>订单簿</Text>
              <Row gutter={16} style={{ marginTop: 8 }}>
                <Col span={12}>
                  <Text type="success" strong>买盘 (Bids)</Text>
                  <Table
                    dataSource={(orderBook.bids || []).slice(0, 10)}
                    columns={[
                      { title: '价格', dataIndex: 'price', key: 'price' },
                      { title: '数量', dataIndex: 'size', key: 'size' },
                    ]}
                    pagination={false}
                    size="small"
                    rowKey={(_, i) => `bid-${i}`}
                  />
                </Col>
                <Col span={12}>
                  <Text type="danger" strong>卖盘 (Asks)</Text>
                  <Table
                    dataSource={(orderBook.asks || []).slice(0, 10)}
                    columns={[
                      { title: '价格', dataIndex: 'price', key: 'price' },
                      { title: '数量', dataIndex: 'size', key: 'size' },
                    ]}
                    pagination={false}
                    size="small"
                    rowKey={(_, i) => `ask-${i}`}
                  />
                </Col>
              </Row>
            </div>
          )}
        </Card>
      </Col>

      <Col span={12}>
        <Card title="下单" size="small">
          <Form form={orderForm} layout="vertical" onFinish={handlePlaceOrder}>
            <Form.Item
              name="passportIds"
              label="选择钱包 (可多选, 首次下单自动注册 API Key)"
              rules={[{ required: true, message: '请选择钱包' }]}
            >
              <Select
                mode="multiple"
                placeholder="支持多选, 勾选多个钱包后同时下单"
                optionFilterProp="children"
                maxTagCount="responsive"
              >
                {botStatuses.map(b => (
                  <Option
                    key={b.passportId}
                    value={b.passportId}
                  >
                    #{b.passportId} {b.label ? `(${b.label})` : ''}
                    {' '}{b.walletAddress?.slice(0, 8)}...
                    {' '}pUSD: {(b.usdcBalance || 0).toFixed(2)}
                    {!b.apiKeyRegistered && ' 🔑首次会自动注册'}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="side" label="方向" rules={[{ required: true }]}>
              <Radio.Group>
                <Radio.Button value="BUY" style={{ color: COLORS.success }}>
                  买入 (BUY)
                </Radio.Button>
                <Radio.Button value="SELL" style={{ color: COLORS.error }}>
                  卖出 (SELL)
                </Radio.Button>
              </Radio.Group>
            </Form.Item>

            <Form.Item name="price" label="价格 (0.01-0.99)" rules={[{ required: true }]}>
              <InputNumber min={0.01} max={0.99} step={0.01} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item name="size" label="数量" rules={[{ required: true }]}>
              <InputNumber min={1} step={10} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item name="orderType" label="订单类型" initialValue="GTC">
              <Select>
                <Option value="GTC">长期挂单 (GTC) — 不成交就一直挂着, 直到手动撤单</Option>
                <Option value="FOK">全部立即成交或取消 (FOK) — 不能部分成交</Option>
                <Option value="FAK">立即成交剩余取消 (FAK/IOC) — 能成交多少算多少, 剩余取消</Option>
              </Select>
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<SwapOutlined />}
                disabled={!selectedTokenId}
                block
              >
                下单
              </Button>
            </Form.Item>
          </Form>
        </Card>

        {currentTask && (
          <Card
            title={<>任务进度 <Text type="secondary" style={{ fontSize: 12 }}>{currentTask.taskId}</Text></>}
            size="small"
            style={{ marginTop: 16 }}
            extra={
              <Button size="small" type="text" onClick={() => {
                stopTaskPolling();
                setCurrentTask(null);
              }}>
                关闭
              </Button>
            }
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text>类型: {currentTask.taskType} {statusTag(currentTask.status)}</Text>
              <Progress
                percent={Math.round(
                  ((currentTask.successCount + currentTask.failedCount) / Math.max(currentTask.totalCount, 1)) * 100
                )}
                status={currentTask.status === 'failed' ? 'exception' : undefined}
              />
              <Text>成功: {currentTask.successCount} / 失败: {currentTask.failedCount} / 总计: {currentTask.totalCount}</Text>
            </Space>
          </Card>
        )}
      </Col>
    </Row>
    </>
  );

  const renderOrdersTab = () => (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Select
          placeholder="选择 Bot 查看挂单"
          style={{ width: 300 }}
          onChange={(val: number) => {
            setOrderBotId(val);
            fetchOpenOrders(val);
            fetchOrderHistory(val);
          }}
        >
          {botStatuses.map(b => (
            <Option key={b.passportId} value={b.passportId}>
              #{b.passportId} {b.label ? `(${b.label})` : ''} {b.walletAddress?.slice(0, 8)}...
            </Option>
          ))}
        </Select>
        {orderBotId && (
          <Popconfirm title="确认撤销该 Bot 的所有挂单?" onConfirm={async () => {
            const res = await apiPost<any>('/api/polymarket/cancel-all', { passportId: orderBotId });
            if (res.code === 0) {
              const d = res.data;
              message.info(`撤销: ${d.cancelled}, 已成交: ${d.alreadyMatched}, 失败: ${d.failed}`);
              fetchOpenOrders(orderBotId);
            }
          }}>
            <Button danger icon={<StopOutlined />}>撤销全部</Button>
          </Popconfirm>
        )}
      </Space>

      <Text strong style={{ display: 'block', marginBottom: 8 }}>当前挂单</Text>
      <Table
        dataSource={openOrders}
        columns={[
          { title: '市场', dataIndex: 'marketTitle', key: 'market', ellipsis: true, width: 200 },
          { title: '方向', dataIndex: 'side', key: 'side', render: (s: string) =>
            <Tag color={s === 'BUY' ? 'green' : 'red'}>{s}</Tag> },
          { title: '价格', dataIndex: 'price', key: 'price' },
          { title: '数量', dataIndex: 'size', key: 'size' },
          { title: '已成交', dataIndex: 'filledSize', key: 'filled' },
          { title: '状态', dataIndex: 'status', key: 'status', render: statusTag },
          { title: '操作', key: 'action', render: (_: any, record: OrderRecord) => (
            <Popconfirm title="确认撤单?" onConfirm={() => handleCancelOrder(record.passportId, record.orderId)}>
              <Button size="small" danger>撤单</Button>
            </Popconfirm>
          )},
        ]}
        pagination={false}
        size="small"
        rowKey="id"
      />

      <Text strong style={{ display: 'block', margin: '16px 0 8px' }}>订单历史</Text>
      <Table
        dataSource={orderHistory}
        columns={[
          { title: '市场', dataIndex: 'marketTitle', key: 'market', ellipsis: true, width: 200 },
          { title: '方向', dataIndex: 'side', key: 'side', render: (s: string) =>
            <Tag color={s === 'BUY' ? 'green' : 'red'}>{s}</Tag> },
          { title: '价格', dataIndex: 'price', key: 'price' },
          { title: '数量', dataIndex: 'size', key: 'size' },
          { title: '已成交', dataIndex: 'filledSize', key: 'filled' },
          { title: '状态', dataIndex: 'status', key: 'status', render: statusTag },
          { title: '时间', dataIndex: 'createTime', key: 'time' },
        ]}
        pagination={{ pageSize: 20 }}
        size="small"
        rowKey="id"
      />
    </div>
  );

  const renderBalanceTab = () => (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<SyncOutlined />} onClick={fetchBotStatuses}>刷新</Button>
      </Space>
      <Table
        dataSource={botStatuses}
        columns={[
          { title: 'ID', dataIndex: 'passportId', key: 'id', width: 80 },
          {
            title: 'Proxy 地址 (持仓)',
            dataIndex: 'proxyAddress',
            key: 'proxy',
            render: (v: string) => v ? (
              <Space size={4}>
                <Text code style={{ fontSize: 12 }}>{v.slice(0, 8)}...{v.slice(-6)}</Text>
                <a href={polymarketUserLink(v)} target="_blank" rel="noopener noreferrer">🌐</a>
                <a href={polygonscanLink(v)} target="_blank" rel="noopener noreferrer">🔎</a>
              </Space>
            ) : <Tag>未创建</Tag>,
          },
          { title: 'pUSD 余额', dataIndex: 'usdcBalance', key: 'balance',
            render: (v: number) => <Text strong>{v?.toFixed(2) || '0.00'}</Text> },
          { title: 'API Key', dataIndex: 'apiKeyRegistered', key: 'key',
            render: (v: boolean) => v ?
              <Tag color="success">已注册</Tag> : <Tag>自动注册</Tag> },
          {
            title: 'EOA 地址 (签名)',
            dataIndex: 'walletAddress',
            key: 'addr',
            render: (v: string) => v ? (
              <Space size={4}>
                <Text code style={{ fontSize: 12 }}>{v.slice(0, 8)}...{v.slice(-6)}</Text>
                <a href={polygonscanLink(v)} target="_blank" rel="noopener noreferrer">🔎</a>
              </Space>
            ) : '-',
          },
          { title: '同步时间', dataIndex: 'lastSyncedAt', key: 'sync' },
        ]}
        pagination={false}
        size="small"
        rowKey="passportId"
      />
    </div>
  );

  const [distributeAmount, setDistributeAmount] = useState<number>(100);

  // 分发/回收 Modal 状态
  const [distributeModalOpen, setDistributeModalOpen] = useState(false);
  const [collectModalOpen, setCollectModalOpen] = useState(false);
  const [masterPrivateKey, setMasterPrivateKey] = useState<string>('');
  const [masterAddrInput, setMasterAddrInput] = useState<string>('');
  const [verifyingKey, setVerifyingKey] = useState(false);
  const [verifiedWallet, setVerifiedWallet] = useState<{ address: string; balance: number } | null>(null);

  const verifyPrivateKey = async () => {
    if (!masterPrivateKey || masterPrivateKey.length < 64) {
      message.warning('请输入完整的私钥');
      return;
    }
    try {
      setVerifyingKey(true);
      const res = await apiPost<any>('/api/polymarket/wallet-info', { privateKey: masterPrivateKey });
      if (res.code === 0 && res.data) {
        setVerifiedWallet(res.data);
      } else {
        message.error(res.msg);
      }
    } catch (e: any) {
      message.error('验证失败: ' + e.message);
    } finally {
      setVerifyingKey(false);
    }
  };

  const confirmDistribute = async () => {
    if (!verifiedWallet) {
      message.warning('请先验证私钥');
      return;
    }
    const needed = distributeAmount * selectedBotIds.length;
    if (verifiedWallet.balance < needed) {
      message.error(`主钱包余额不足, 需 ${needed} pUSD, 实际 ${verifiedWallet.balance}`);
      return;
    }
    await handleBatchAction('distribute', {
      amount: distributeAmount,
      masterPrivateKey,
    });
    // 清理敏感数据
    setMasterPrivateKey('');
    setVerifiedWallet(null);
    setDistributeModalOpen(false);
  };

  const confirmCollect = async () => {
    if (!masterAddrInput || !masterAddrInput.startsWith('0x')) {
      message.warning('请输入有效的主钱包地址');
      return;
    }
    await handleBatchAction('collect', { masterAddress: masterAddrInput });
    setCollectModalOpen(false);
  };

  // ===== Bridge 充值 (USDC → pUSD) =====
  const [bridgeModalOpen, setBridgeModalOpen] = useState(false);
  const [bridgeTargetAddr, setBridgeTargetAddr] = useState<string>('');
  const [bridgeAddresses, setBridgeAddresses] = useState<Record<string, any> | null>(null);
  const [bridgeLoading, setBridgeLoading] = useState(false);

  const fetchDepositAddresses = async (walletAddress: string) => {
    if (!walletAddress || !walletAddress.startsWith('0x')) {
      message.warning('请输入有效的钱包地址');
      return;
    }
    try {
      setBridgeLoading(true);
      const res = await apiGet<any>(`/api/polymarket/deposit-addresses?walletAddress=${walletAddress}`);
      if (res.code === 0 && res.data) {
        setBridgeAddresses(res.data);
      } else {
        message.error(res.msg);
      }
    } catch (e: any) {
      message.error('获取存款地址失败: ' + e.message);
    } finally {
      setBridgeLoading(false);
    }
  };

  const renderSetupTab = () => (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Bot 准备 — 交易前的一次性配置"
        description={
          <div>
            <p style={{ margin: '4px 0' }}>
              钱包能在"<b>交易操作</b>"页面下单，只需要满足 <b>2 个条件</b>：
            </p>
            <ol style={{ margin: '4px 0 4px 20px', padding: 0 }}>
              <li>
                <b>Proxy 钱包已创建</b> — 首次注册 API Key 时自动创建, Polymarket 真正的持仓地址
              </li>
              <li>
                <b>Proxy 有 pUSD</b> — 买单需要钱, 用"批量分发"或"USDC 跨链充值"把 pUSD 充到 Proxy 地址
              </li>
            </ol>
            <p style={{ margin: '4px 0', color: '#888' }}>
              运营不需要关心: API Key / gas 费 / 合约授权
            </p>
            <ul style={{ margin: '2px 0 4px 20px', padding: 0, color: '#888' }}>
              <li><b>API Key</b>: 首次下单时自动注册, 同时创建 Proxy 钱包</li>
              <li><b>gas 费</b>: 下单/撤单走 CLOB HTTP API, Polymarket 代付</li>
              <li><b>合约授权</b> (pUSD / CT): Polymarket 初始化 Proxy 时自动完成</li>
            </ul>
            <p style={{ margin: '4px 0', color: '#888' }}>
              建议操作顺序: ① 勾选钱包 → ② "批量分发 pUSD" → ③ 去"交易操作"下单
            </p>
          </div>
        }
      />
      <Card title="钱包列表" size="small">
        <Space style={{ marginBottom: 16 }} wrap>
          <Tooltip title="首次下单会自动注册, 这个按钮是兜底. 调用 Polymarket /auth/api-key, 同时创建 Proxy 钱包">
            <Button type="primary" icon={<KeyOutlined />}
              onClick={() => handleBatchAction('register-keys')}
              disabled={selectedBotIds.length === 0}>
              批量注册 API Key
            </Button>
          </Tooltip>
          <InputNumber
            min={0} max={10000} value={distributeAmount}
            onChange={v => setDistributeAmount(v || 0)}
            addonAfter="pUSD/Bot"
            style={{ width: 180 }}
          />
          <Button icon={<SendOutlined />}
            onClick={() => setDistributeModalOpen(true)}
            disabled={selectedBotIds.length === 0}>
            批量分发
          </Button>
          <Button
            icon={<WalletOutlined />}
            onClick={() => {
              setBridgeAddresses(null);
              setBridgeTargetAddr('');
              setBridgeModalOpen(true);
            }}
          >
            USDC 跨链充值
          </Button>
          <Button icon={<RollbackOutlined />}
            onClick={() => setCollectModalOpen(true)}
            disabled={selectedBotIds.length === 0}>
            批量回收
          </Button>
          <Button icon={<SyncOutlined />}
            onClick={() => handleBatchAction('sync')}>
            同步余额
          </Button>
        </Space>

        <Table
          dataSource={botStatuses}
          rowSelection={{
            selectedRowKeys: selectedBotIds,
            onChange: (keys) => setSelectedBotIds(keys as number[]),
          }}
          columns={[
            { title: 'ID', dataIndex: 'passportId', key: 'id', width: 60 },
            {
              title: <span>Proxy 地址 <Text type="secondary" style={{ fontSize: 11 }}>(持仓)</Text></span>,
              dataIndex: 'proxyAddress',
              key: 'proxy',
              render: (v: string) => v ? (
                <Space size={4}>
                  <Text code style={{ fontSize: 12 }}>{v.slice(0, 8)}...{v.slice(-6)}</Text>
                  <a href={polymarketUserLink(v)} target="_blank" rel="noopener noreferrer"
                     title="Polymarket Profile">🌐</a>
                  <a href={polygonscanLink(v)} target="_blank" rel="noopener noreferrer"
                     title="Polygonscan">🔎</a>
                </Space>
              ) : <Tag>注册后生成</Tag>,
            },
            { title: 'pUSD 余额', dataIndex: 'usdcBalance', key: 'balance', width: 120,
              render: (v: number) => <Text strong>{(v || 0).toFixed(2)}</Text> },
            { title: 'API Key', dataIndex: 'apiKeyRegistered', key: 'key', width: 100,
              render: (v: boolean) => v ?
                <Tag color="success">已注册</Tag> : <Tag>自动注册</Tag> },
            {
              title: <span>EOA 地址 <Text type="secondary" style={{ fontSize: 11 }}>(签名)</Text></span>,
              dataIndex: 'walletAddress',
              key: 'eoa',
              render: (v: string) => v ? (
                <Space size={4}>
                  <Text code style={{ fontSize: 12 }}>{v.slice(0, 8)}...{v.slice(-6)}</Text>
                  <a href={polygonscanLink(v)} target="_blank" rel="noopener noreferrer"
                     title="Polygonscan">🔎</a>
                </Space>
              ) : '-',
            },
          ]}
          pagination={{ pageSize: 20 }}
          size="small"
          rowKey="passportId"
        />
      </Card>

      {currentTask && (
        <Card title="任务进度" size="small" style={{ marginTop: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text>类型: {currentTask.taskType} {statusTag(currentTask.status)}</Text>
            <Progress
              percent={Math.round(
                ((currentTask.successCount + currentTask.failedCount) / Math.max(currentTask.totalCount, 1)) * 100
              )}
            />
            <Text>成功: {currentTask.successCount} / 失败: {currentTask.failedCount} / 总计: {currentTask.totalCount}</Text>
          </Space>
        </Card>
      )}
    </div>
  );

  return (
    <div style={{ padding: '0 4px' }}>
      <div style={{ marginBottom: 16 }}>
        <Space align="center">
          <SwapOutlined style={{ fontSize: 24, color: COLORS.primary }} />
          <span style={{ fontSize: 20, fontWeight: 600 }}>Polymarket 交易</span>
        </Space>
      </div>

      <Tabs defaultActiveKey="trade" items={[
        { key: 'trade', label: '交易操作', children: renderTradeTab() },
        { key: 'orders', label: '挂单管理', children: renderOrdersTab() },
        { key: 'balance', label: '持仓 & 余额', children: renderBalanceTab() },
        { key: 'setup', label: 'Bot 准备', children: renderSetupTab() },
      ]} />

      {/* 批量分发弹窗: 输入主钱包私钥 */}
      <Modal
        title="批量分发 pUSD"
        open={distributeModalOpen}
        onCancel={() => {
          setDistributeModalOpen(false);
          setMasterPrivateKey('');
          setVerifiedWallet(null);
        }}
        onOk={confirmDistribute}
        okText="确认分发"
        cancelText="取消"
        okButtonProps={{ disabled: !verifiedWallet }}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="安全提示"
          description="主钱包私钥仅在本次操作中使用, 不会保存到服务器。请确认在可信网络环境下操作。"
        />

        <Form layout="vertical">
          <Form.Item label="主钱包私钥">
            <Input.Password
              placeholder="0x..."
              value={masterPrivateKey}
              onChange={e => {
                setMasterPrivateKey(e.target.value);
                setVerifiedWallet(null);
              }}
              autoComplete="off"
            />
          </Form.Item>

          <Button onClick={verifyPrivateKey} loading={verifyingKey} disabled={!masterPrivateKey}>
            验证私钥 & 查询余额
          </Button>

          {verifiedWallet && (
            <Card size="small" style={{ marginTop: 16 }}>
              <Row gutter={16}>
                <Col span={24}>
                  <Text strong>钱包地址:</Text> <Text code>{verifiedWallet.address}</Text>
                </Col>
                <Col span={24} style={{ marginTop: 8 }}>
                  <Statistic
                    title="pUSD 余额"
                    value={verifiedWallet.balance}
                    precision={2}
                    suffix="pUSD"
                  />
                </Col>
              </Row>
            </Card>
          )}

          <div style={{ marginTop: 16, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
            <Text style={{ display: 'block' }}>
              将分发 <b>{distributeAmount} pUSD</b> × {selectedBotIds.length} 个钱包 = <Text strong type="success">{distributeAmount * selectedBotIds.length} pUSD</Text>
            </Text>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
              pUSD 会直接转到每个钱包的 Proxy 地址 (Polymarket 持仓地址)
            </Text>
          </div>
        </Form>
      </Modal>

      {/* 批量回收弹窗: 输入主钱包地址 (不需要私钥, 因为是 Bot 签名) */}
      <Modal
        title="批量回收 pUSD"
        open={collectModalOpen}
        onCancel={() => {
          setCollectModalOpen(false);
          setMasterAddrInput('');
        }}
        onOk={confirmCollect}
        okText="确认回收"
        cancelText="取消"
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="将从选中的 Bot 钱包转出 pUSD 到主钱包地址 (保留 0.1 pUSD 做 gas)"
        />
        <Form layout="vertical">
          <Form.Item label="主钱包地址 (接收方)">
            <Input
              placeholder="0x..."
              value={masterAddrInput}
              onChange={e => setMasterAddrInput(e.target.value)}
            />
          </Form.Item>
          <Text>将从 {selectedBotIds.length} 个 Bot 钱包回收余额到上述地址</Text>
        </Form>
      </Modal>

      {/* Bridge 充值弹窗: USDC → pUSD 跨链 */}
      <Modal
        title="USDC 跨链充值 (自动转 pUSD)"
        open={bridgeModalOpen}
        onCancel={() => {
          setBridgeModalOpen(false);
          setBridgeAddresses(null);
          setBridgeTargetAddr('');
        }}
        footer={null}
        width={640}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Polymarket 官方 Bridge API"
          description={
            <div>
              <p style={{ margin: '4px 0' }}>
                流程: ① 输入目标钱包地址 → ② 获取多链存款地址 → ③ 从外部钱包 (MetaMask/交易所提现) 发 <b>USDC</b> 到地址 → ④ Polymarket 自动桥接并转换为 <b>pUSD</b> 到目标地址
              </p>
              <p style={{ margin: '4px 0', color: '#888' }}>
                仅支持 USDC / USDC.e, <b>不支持 USDT / ETH / MATIC</b> 直接充值
              </p>
            </div>
          }
        />

        <Form layout="vertical">
          <Form.Item label="目标钱包地址 (主钱包 或 Bot 钱包)">
            <Space.Compact style={{ width: '100%' }}>
              <Input
                placeholder="0x..."
                value={bridgeTargetAddr}
                onChange={e => setBridgeTargetAddr(e.target.value)}
              />
              <Button
                type="primary"
                loading={bridgeLoading}
                onClick={() => fetchDepositAddresses(bridgeTargetAddr)}
              >
                获取存款地址
              </Button>
            </Space.Compact>
          </Form.Item>
        </Form>

        {bridgeAddresses && (
          <div style={{ marginTop: 16 }}>
            <Text strong>请从外部钱包发 USDC 到以下任一地址</Text>
            <Table
              dataSource={Object.entries(bridgeAddresses).map(([chain, addr]) => ({
                chain,
                address: typeof addr === 'string' ? addr : JSON.stringify(addr),
              }))}
              columns={[
                { title: '链', dataIndex: 'chain', key: 'chain', width: 100 },
                {
                  title: '存款地址',
                  dataIndex: 'address',
                  key: 'address',
                  render: (v: string) => (
                    <Text copyable={{ text: v }} code style={{ fontSize: 12 }}>{v}</Text>
                  ),
                },
              ]}
              pagination={false}
              size="small"
              rowKey="chain"
              style={{ marginTop: 8 }}
            />
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
              evm = Ethereum/Polygon/Arbitrum/Base/Optimism, svm = Solana, btc = Bitcoin, tvm = Tron
            </Text>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Polymarket;
